import { WorkActionCard } from '@/components/work/WorkActionCard';
import { WorkActionsProvider } from '@/components/work/WorkActionsContext';
import { getSubjectWorkActions } from '@/lib/work-actions';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Mail,
  PhoneCall,
  CircleStop,
  Play,
  RefreshCw,
  Sparkles,
  Target,
  Users,
  ListChecks,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ToolActionRequiredSheet } from '@/components/tools/ToolActionRequiredSheet';
import { AppSpecPage } from '@/components/apps/AppSpecPage';
import { ImportMenu } from '@/components/outreach/ImportMenu';
import { type ImportSource, TableImportDrawer } from '@/components/outreach/TableImportDrawer';
import { takeImportText } from '@/lib/table-import-handoff';
import { defaultAppSpec, type AppPage } from '@shared/types/app-spec';
import { loadAppSpec, type StoredAppSpec } from '@/lib/app-spec';
import { AppObjectDrawer } from '@/components/apps/AppObjectDrawer';
import type { AppSummary } from '@/lib/app-access';
import { APP_STATUS_LABELS } from '@/lib/app-manage';
import { AppPageManageMenu } from '@/components/apps/AppManageMenu';
import { ToolLaunchDialog } from '@/components/tools/ToolLaunchDialog';
import { ToolExecutionTracePanel } from '@/components/tools/ToolExecutionTracePanel';
import { ToolRecentActivityCard } from '@/components/tools/ToolRecentActivityCard';
import { ToolResultPageRenderer } from '@/components/tools/ToolResultPageRenderer';
import { readDocumentOutput } from '@/lib/work-document-output';
import { ToolThreadContextCard } from '@/components/tools/ToolThreadContextCard';
import { useThreadStateRealtime } from '@/components/activity/useThreadStateRealtime';
import { useProposalExpiryRefresh } from '@/components/activity/useProposalExpiryRefresh';
import { useRegisterAppAssistantContext, type AppAssistantContextSnapshot } from '@/lib/app-assistant-context';
import { getActivitySubjectDetail } from '@/lib/activity-feed';
import { invokeDashboardExecute } from '@/lib/dashboard-execute';
import { toDashboardUiError, type DashboardUiError } from '@/lib/dashboard-errors';
import { useExecutionChatState } from '@/lib/hooks/useExecutionChatState';
import {
  getExecutionTraceMessageText,
  useToolExecutionTrace,
} from '@/lib/hooks/useToolExecutionTrace';
import { bootstrapPrebuiltTools, resolveToolRuntime } from '@/lib/prebuilt-tools';
import { supabase } from '@/lib/supabase';
import {
  resolveThreadProposalHandoff,
  resolveToolHandoffOrigin,
  resolveToolLaunchContext,
  resolveToolPrefill,
  resolveToolLaunchRequested,
} from '@/lib/tool-handoff';
import { focusThreadTopic } from '@/lib/thread-topic-focus';
import { buildRunResultTabs, resultTabScopeSuffix, type ToolResultTab } from '@/lib/tool-result-composition';
import { parseResultPayload } from '@shared/types/result-composition';
import {
  listToolRuns,
  updateRunResultScope,
  type RunResultScope,
  type ToolRunListItem,
} from '@/lib/tool-runs';
import { cn } from '@/lib/utils';
import { getResultTabLabel, isRedundantVisibleLabel, sweepActivitySummary, sweepBusinessVocabulary, sweepSerializedBusinessText } from '@/lib/ui-editorial-sweeper';
import { buildSupportMailto } from '@/lib/support-contact';
import type { DashboardComposition } from '@shared/types/dashboard-composition';
import type { Dashboard } from '@shared/types/dashboards';
import type { SubjectActivitySummary } from '@shared/types/activity-spine';
import type { ActivitySubjectDetail } from '@shared/types/activity-feed';
import type { FlowExecutionStatus } from '@shared/types/flow-executions';
import type { Flow, FlowInput } from '@shared/types/flows';
import type { ProductDiagnosisAction } from '@shared/types/product-diagnosis';
import { PREBUILT_TOOL_KEYS } from '@shared/types/prebuilt-tools';
import type {
  PrebuiltToolKey,
  ToolPageMode,
  ToolRuntimePublicState,
} from '@shared/types/prebuilt-tools';
import {
  getPrebuiltToolManifest,
  type ToolFieldDefinition,
  type ToolIconKey,
  type ToolSectionId,
} from '@shared/types/tool-manifest';
import {
  normalizeToolSurfaceSpec,
  type ToolSurfaceSpec,
  type ToolSurfaceWorkspaceTabId,
} from '@shared/types/tool-surface';
import type { AnyViewEnvelope, ViewType } from '@shared/types/views';
import { isViewType } from '@shared/types/views';

const isPrebuiltToolKey = (value: string | undefined): value is PrebuiltToolKey =>
  PREBUILT_TOOL_KEYS.some((toolKey) => toolKey === value);

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EXECUTION_TRACE_TAB_ID = '__execution_trace';
const DASHBOARD_RESULT_TAB_ID = '__dashboard_result';
const MAX_ASSISTANT_VISIBLE_SUMMARY_LENGTH = 1_900;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

interface ToolPageMeta {
  title: string;
  promise: string;
  description: string;
  pageMode: ToolPageMode;
  sectionOrder?: ToolSectionId[];
  icon: LucideIcon;
  launchLabel: string;
  composerTitle: string;
  composerDescription: string;
  submitLabel: string;
  fields: ToolFieldDefinition[];
  defaultValues: Record<string, string>;
}

type ToolPageSubmission =
  | {
      kind: 'paste_transcript';
      transcript: string;
    }
  | {
      kind: 'provide_inputs';
      inputValues: Record<string, string>;
    };

type ToolWorkspaceTabId = ToolSurfaceWorkspaceTabId;
/** F5a/F6a: an app's pages (`page:<id>`, from its description) come first when it has a list. */
type ToolPageTabId = ToolWorkspaceTabId | `page:${string}`;

interface ToolWorkspaceTab {
  id: ToolPageTabId;
  label: string;
  icon: LucideIcon;
}

type ToolResultSurfaceTab =
  | {
      id: string;
      label: string;
      kind: 'result';
      scope: ToolResultTab['scope'];
    }
  | {
      id: typeof EXECUTION_TRACE_TAB_ID;
      label: string;
      kind: 'execution';
      scope: 'run';
    };

const TOOL_ICON_BY_KEY: Record<ToolIconKey, LucideIcon> = {
  bar_chart_3: BarChart3,
  building_2: Building2,
  calendar_clock: CalendarClock,
  mail: Mail,
  phone_call: PhoneCall,
};

const WORKSPACE_TAB_ICON_BY_ID: Record<ToolWorkspaceTabId, LucideIcon> = {
  tasks: ClipboardList,
  library: BookOpen,
  team: Users,
};

const toWorkspaceTabs = (
  tabs: ToolSurfaceSpec['workspace']['tabs']
): ToolWorkspaceTab[] =>
  tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: WORKSPACE_TAB_ICON_BY_ID[tab.id],
  }));

const getToolWorkspaceTabs = (toolKey: PrebuiltToolKey | null): ToolWorkspaceTab[] => {
  if (toolKey === 'analyze_call') {
    return [
      { id: 'tasks', label: 'Appels', icon: PhoneCall },
      { id: 'library', label: 'Bibliothèques', icon: BookOpen },
      { id: 'team', label: 'Coaching', icon: Target },
    ];
  }

  if (toolKey === 'draft_followup') {
    return [
      { id: 'tasks', label: 'Emails', icon: Mail },
      { id: 'library', label: 'Bibliothèque', icon: BookOpen },
      { id: 'team', label: 'Équipe', icon: Users },
    ];
  }

  if (toolKey === 'prepare_meeting') {
    return [
      { id: 'tasks', label: 'Briefs', icon: CalendarClock },
      { id: 'library', label: 'Bibliothèque', icon: BookOpen },
      { id: 'team', label: 'Équipe', icon: Users },
    ];
  }

  if (toolKey === 'pipeline_health_check') {
    return [
      { id: 'tasks', label: 'Analyses', icon: BarChart3 },
      { id: 'library', label: 'Vues', icon: BookOpen },
      { id: 'team', label: 'Équipe', icon: Users },
    ];
  }

  if (toolKey === 'qualify_account') {
    return [
      { id: 'tasks', label: 'Comptes', icon: Building2 },
      { id: 'library', label: 'Signaux', icon: BookOpen },
      { id: 'team', label: 'Équipe', icon: Users },
    ];
  }

  return [{ id: 'tasks', label: 'Tâches', icon: ClipboardList }];
};

const getUserFlowToolSurface = (flow: Flow): ToolSurfaceSpec =>
  normalizeToolSurfaceSpec(flow.tool_surface, { viewType: flow.view_type });

const getUserFlowWorkspaceTabs = (flow: Flow): ToolWorkspaceTab[] =>
  toWorkspaceTabs(getUserFlowToolSurface(flow).workspace.tabs);

const getToolPageMeta = (toolKey: PrebuiltToolKey): ToolPageMeta => {
  const manifest = getPrebuiltToolManifest(toolKey);

  return {
    title: manifest.product.title,
    promise: manifest.product.promise,
    description: manifest.product.description,
    pageMode: manifest.output.pageMode,
    sectionOrder: manifest.output.sectionOrder,
    icon: TOOL_ICON_BY_KEY[manifest.launch.icon],
    launchLabel: manifest.launch.launchLabel,
    composerTitle: manifest.launch.composerTitle,
    composerDescription: manifest.launch.composerDescription,
    submitLabel: manifest.launch.submitLabel,
    fields: manifest.launch.fields,
    defaultValues: manifest.launch.defaultValues,
  };
};

const toToolFieldType = (inputType: FlowInput['type']): ToolFieldDefinition['type'] => {
  if (inputType === 'textarea' || inputType === 'select' || inputType === 'email') {
    return inputType;
  }

  return 'text';
};

const getUserFlowToolPageMeta = (flow: Flow): ToolPageMeta => {
  const toolSurface = getUserFlowToolSurface(flow);
  const metrics = (flow.output_contract?.metrics ?? [])
    .map((metric) => sweepBusinessVocabulary(metric.label.trim()))
    .filter((label) => label.length > 0)
    .slice(0, 3);
  const fallbackDescription = sweepBusinessVocabulary(flow.description.trim()) || 'Application créée dans Domino.';

  return {
    title: sweepBusinessVocabulary(flow.title),
    promise: fallbackDescription,
    description:
      metrics.length > 0
        ? `Sorties attendues : ${metrics.join(', ')}.`
        : fallbackDescription,
    pageMode: toolSurface.result.pageMode,
    sectionOrder: toolSurface.result.sectionOrder as ToolSectionId[],
    icon: Sparkles,
    launchLabel: 'Utiliser l’application',
    composerTitle: 'Nouvelle utilisation',
    composerDescription:
      'Complétez les informations puis laissez Domino produire le résultat dans cet espace de travail.',
    submitLabel: 'Lancer le travail',
    fields: flow.inputs.map((input) => ({
      key: input.key,
      label: sweepBusinessVocabulary(input.label),
      type: toToolFieldType(input.type),
      required: input.required,
      placeholder: input.defaultValue,
      ...(input.description ? { description: sweepBusinessVocabulary(input.description) } : {}),
      ...(input.type === 'textarea' ? { rows: 6 } : {}),
      ...(input.options ? { options: input.options } : {}),
    })),
    defaultValues: flow.inputs.reduce<Record<string, string>>((accumulator, input) => {
      accumulator[input.key] = input.defaultValue ?? input.options?.[0]?.value ?? '';
      return accumulator;
    }, {}),
  };
};

const isAnyViewEnvelope = (value: unknown): value is AnyViewEnvelope =>
  isRecord(value) &&
  isViewType(value.view_type) &&
  typeof value.schema_version === 'string' &&
  isRecord(value.data);

const clampText = (value: string, maxLength: number): string => {
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const getExecutionSummaryNarration = (value: unknown): string | null => {
  if (!isRecord(value) || typeof value.narration !== 'string') return null;
  const narration = value.narration.trim();
  return narration.length > 0 ? narration : null;
};

const formatRunTimestamp = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getVisibleRunTitle = (value: string): string =>
  sweepBusinessVocabulary(sweepSerializedBusinessText(value));

const getVisibleRunSummary = (run: ToolRunListItem): string | null => {
  const summary = sweepActivitySummary(run.summary, run.status === 'failed');
  return summary ? sweepBusinessVocabulary(summary) : null;
};

const getRunTimestamp = (run: ToolRunListItem): string | null =>
  run.completedAt ?? run.startedAt ?? run.updatedAt;

const isActiveRunStatus = (
  status: FlowExecutionStatus
): status is Extract<FlowExecutionStatus, 'pending' | 'running' | 'paused'> =>
  status === 'pending' || status === 'running' || status === 'paused';

const getRunStatusMeta = (
  status: FlowExecutionStatus
): {
  label: string;
  className: string;
} => {
  switch (status) {
    case 'paused':
      return {
        label: 'En attente d’action',
        className: 'text-[var(--domino-warning)]',
      };
    case 'running':
      return {
        label: 'En cours',
        className: 'text-domino-aubergine',
      };
    case 'pending':
      return {
        label: 'En file',
        className: 'text-domino-inkSoft',
      };
    case 'failed':
      return {
        label: 'Échec',
        className: 'text-red-700',
      };
    case 'cancelled':
      return {
        label: 'Annulé',
        className: 'text-domino-inkSoft',
      };
    case 'completed':
    default:
      return {
        label: 'Terminé',
        className: 'text-emerald-800',
      };
  }
};

const loadDashboardById = async (dashboardId: string): Promise<Dashboard> => {
  const { data, error } = await supabase
    .from('dashboards')
    .select('*')
    .eq('id', dashboardId)
    .is('deleted_at', null)
    .single();

  if (error || !data) {
    throw error ?? new Error('Impossible de charger le dashboard du tool.');
  }

  return data as Dashboard;
};

interface ToolPageProps {
  /** F5-0: the flow of the app opened at /apps/:key (otherwise read from /tools/:toolKey). */
  flowId?: string;
  app?: AppSummary;
  /** An object of the app opened at /apps/:key/o/:recordId: a drawer over the app's page. */
  recordId?: string;
}

export const ToolPage = ({ flowId: appFlowId, app: openedApp, recordId }: ToolPageProps = {}) => {
  const navigate = useNavigate();
  // The app as changed from its « … » (access, publication, archive).
  const [app, setApp] = useState(openedApp);
  const location = useLocation();
  const { toolKey: routeToolKey } = useParams<{ toolKey: string }>();
  const toolRefParam = appFlowId ?? routeToolKey;
  // The object page tells the chat what it shows; the tool page is the only one to register it.
  const [objectContext, setObjectContext] = useState<AppAssistantContextSnapshot | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const prebuiltToolKey: PrebuiltToolKey | null = isPrebuiltToolKey(toolRefParam)
    ? toolRefParam
    : null;
  const userFlowId =
    !prebuiltToolKey && toolRefParam && UUID_PATTERN.test(toolRefParam)
      ? toolRefParam
      : null;
  const toolRef = prebuiltToolKey ?? userFlowId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DashboardUiError | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isTenantManager, setIsTenantManager] = useState(false);
  const [userFlow, setUserFlow] = useState<Flow | null>(null);
  const [userFlowLoading, setUserFlowLoading] = useState(false);
  const [toolRuntimeState, setToolRuntimeState] = useState<ToolRuntimePublicState | null>(
    null
  );
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [composition, setComposition] = useState<DashboardComposition | null>(null);
  const [viewEnvelopesByInstanceId, setViewEnvelopesByInstanceId] = useState<
    Record<string, AnyViewEnvelope | null>
  >({});
  const [flowResultTabs, setFlowResultTabs] = useState<ToolResultTab[]>([]);
  const [activeFlowResultTabId, setActiveFlowResultTabId] = useState<string | null>(null);
  const [flowResultSource, setFlowResultSource] = useState<{
    runId: string;
    composite: boolean;
  } | null>(null);
  const [runResultScope, setRunResultScope] = useState<RunResultScope | null>(null);
  const [runScopeSaving, setRunScopeSaving] = useState(false);
  const [selectedExecutionActivitySummary, setSelectedExecutionActivitySummary] =
    useState<SubjectActivitySummary | null>(null);
  const [mineRuns, setMineRuns] = useState<ToolRunListItem[]>([]);
  const [teamRuns, setTeamRuns] = useState<ToolRunListItem[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const runListRequestIdRef = useRef(0);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeSubmitting, setRuntimeSubmitting] = useState(false);
  const [toolFormValues, setToolFormValues] = useState<Record<string, string>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [importSource, setImportSource] = useState<ImportSource | null>(null);
  const [importText, setImportText] = useState('');
  const [importApp, setImportApp] = useState<{ appKey: string; statuses: string[] } | null>(null);
  const [importCampaignName, setImportCampaignName] = useState<string | null>(null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] =
    useState<ToolPageTabId>('tasks');
  const [appList, setAppList] = useState<{ available: boolean; title: string | null; hasReviewGrid: boolean } | null>(null);
  const reportAppList = useCallback((available: boolean, title: string | null, hasReviewGrid: boolean) => {
    setAppList((current) => {
      // The list opens first once, unless the address asks for a run (an object link opens it too).
      const params = new URLSearchParams(window.location.search);
      // « page: » is not a tab: the first page of the app's description takes its place.
      if (!current && available && (params.get('record') || !params.get('run'))) setActiveWorkspaceTab('page:');
      return { available, title, hasReviewGrid };
    });
  }, []);
  // F6a: the app's description; without one, its current screens.
  const [storedAppSpec, setStoredAppSpec] = useState<StoredAppSpec | null>(null);
  useEffect(() => {
    let active = true;
    if (!app?.id) return;
    void loadAppSpec(app.id)
      .then((value) => { if (active) setStoredAppSpec(value); })
      .catch(() => { if (active) setStoredAppSpec({ spec: null, version: 0, issues: ['description d’app illisible'] }); });
    return () => { active = false; };
  }, [app?.id]);
  const currentAppSpec = useMemo(
    () => storedAppSpec?.spec ?? defaultAppSpec({ hasReviewGrid: Boolean(appList?.hasReviewGrid) }),
    [storedAppSpec, appList?.hasReviewGrid],
  );
  const appPages = useMemo<AppPage[]>(() => currentAppSpec.pages, [currentAppSpec]);
  // What the chat proposed to import opens the import drawer, filled: a table (?import=1) or an app's
  // results (?import=app&depuis=<app>&statuts=a,b), into a new campaign it may name (&campagne_nom=…).
  useEffect(() => {
    const asked = searchParams.get('import');
    if (!app || !asked) return;
    setImportCampaignName(searchParams.get('campagne_nom'));
    if (asked === 'app' && searchParams.get('depuis')) {
      setImportApp({ appKey: searchParams.get('depuis')!, statuses: (searchParams.get('statuts') ?? '').split(',').filter(Boolean) });
      setImportSource('app');
    } else if (asked === '1') {
      setImportText(takeImportText(app.key));
      setImportSource('paste');
    }
    const next = new URLSearchParams(searchParams);
    for (const key of ['import', 'depuis', 'statuts', 'campagne_nom']) next.delete(key);
    setSearchParams(next, { replace: true });
  }, [app, searchParams, setSearchParams]);
  // A campaign's address (?campagne=<id>) opens the page that holds the campaigns.
  const askedCampaign = searchParams.get('campagne');
  const campaignsPageId = appPages.find((page) => page.blocks.some((block) => block.type === 'sequence'))?.id ?? null;
  useEffect(() => {
    if (askedCampaign && campaignsPageId && appList?.available) setActiveWorkspaceTab(`page:${campaignsPageId}`);
  }, [askedCampaign, campaignsPageId, appList?.available]);
  const [actionSheetDismissed, setActionSheetDismissed] = useState(false);
  const [threadContext, setThreadContext] = useState<ActivitySubjectDetail | null>(null);
  const [threadContextEnabled, setThreadContextEnabled] = useState(false);
  const bootstrappedToolKeyRef = useRef<PrebuiltToolKey | null>(null);
  const selectedScope = searchParams.get('scope') === 'team' ? 'team' : 'mine';
  const selectedExecutionId = searchParams.get('run');
  const allRuns = useMemo(() => [...mineRuns, ...teamRuns], [mineRuns, teamRuns]);
  const selectedRun = useMemo(
    () => allRuns.find((run) => run.id === selectedExecutionId) ?? null,
    [allRuns, selectedExecutionId]
  );
  const toolPrefill = useMemo(
    () => resolveToolPrefill(searchParams, location.state),
    [location.state, searchParams]
  );
  const toolHandoffOrigin = useMemo(
    () => resolveToolHandoffOrigin(searchParams, location.state),
    [location.state, searchParams]
  );
  const threadProposalHandoff = useMemo(
    () => resolveThreadProposalHandoff(location.state),
    [location.state]
  );
  const toolLaunchContext = useMemo(
    () => resolveToolLaunchContext(location.state),
    [location.state]
  );
  const loadThreadContext = useCallback(
    async (background = false) => {
      const subjectId =
        toolHandoffOrigin?.accountSubjectId ??
        toolHandoffOrigin?.subjectId ??
        threadProposalHandoff?.accountSubjectId ??
        threadProposalHandoff?.subjectId ??
        toolLaunchContext?.accountSubjectId ??
        toolLaunchContext?.subjectId ??
        selectedRun?.accountSubjectId ??
        selectedRun?.subjectId ??
        null;
      if (!subjectId && !selectedExecutionActivitySummary?.subjectKey) {
        setThreadContext(null);
        setThreadContextEnabled(false);
        return;
      }

      try {
        const response = await getActivitySubjectDetail({
          subjectId,
          subjectKey: selectedExecutionActivitySummary?.subjectKey ?? null,
          subjectKind: selectedExecutionActivitySummary?.subjectKind ?? 'company',
          subjectLabel:
            toolHandoffOrigin?.sourceActionLabel ??
            threadProposalHandoff?.topicTitle ??
            selectedExecutionActivitySummary?.subjectLabel ?? 'Compte',
        });
        const detail = response.detail;
        const focusedDetail: ActivitySubjectDetail | null =
          detail && threadProposalHandoff && detail.threadState
            ? {
                ...detail,
                threadState: focusThreadTopic(
                  detail.threadState,
                  threadProposalHandoff.topicId
                ).threadState,
              }
            : detail;
        setThreadContext(focusedDetail);
        setThreadContextEnabled(response.threadLivingCardEnabled === true);
      } catch (loadError) {
        if (!background) {
          setThreadContext(null);
          setThreadContextEnabled(false);
        }
        console.warn('Tool Thread context refresh failed', loadError);
      }
    },
    [
      selectedExecutionActivitySummary,
      selectedRun,
      threadProposalHandoff,
      toolHandoffOrigin,
      toolLaunchContext,
    ]
  );
  useEffect(() => {
    const refresh = () => { void loadThreadContext(true); };
    window.addEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
  }, [loadThreadContext]);
  const refreshThreadContext = useCallback(() => {
    void loadThreadContext(true);
  }, [loadThreadContext]);
  useThreadStateRealtime({
    enabled: threadContextEnabled && Boolean(threadContext?.threadState?.threadId),
    threadId: threadContext?.threadState?.threadId ?? null,
    onRefresh: refreshThreadContext,
  });
  useProposalExpiryRefresh({
    threadStates: [threadContext?.threadState],
    onRefresh: refreshThreadContext,
  });
  const toolMeta = useMemo(() => {
    if (prebuiltToolKey) return getToolPageMeta(prebuiltToolKey);
    if (userFlow) return getUserFlowToolPageMeta(userFlow);
    return null;
  }, [prebuiltToolKey, userFlow]);
  const workspaceTabs = useMemo(() => {
    if (prebuiltToolKey) return getToolWorkspaceTabs(prebuiltToolKey).filter((tab) => tab.id === 'tasks');
    const base = userFlow ? getUserFlowWorkspaceTabs(userFlow).filter((tab) => tab.id === 'tasks') : getToolWorkspaceTabs(null);
    return app && appList?.available
      ? appPages.flatMap((page, index): ToolWorkspaceTab[] => {
        const isRuns = page.blocks.some((block) => block.type === 'runs');
        // The runs keep the tool page's own screen, once.
        if (isRuns) return appPages.findIndex((item) => item.blocks.some((block) => block.type === 'runs')) === index
          ? base.map((tab) => ({ ...tab, label: page.label })) : [];
        const icon = page.blocks[0]?.type === 'records' ? ListChecks : page.blocks.some((block) => block.type === 'team_review') ? Users : page.blocks.some((block) => block.type === 'sequence') ? Send : BarChart3;
        return [{ id: `page:${page.id}` as const, label: page.label, icon }];
      })
      : base;
  }, [prebuiltToolKey, userFlow, app, appList?.available, appPages]);
  const runtimeStatus = toolRuntimeState?.status ?? null;
  const executionTrace = useToolExecutionTrace(selectedRun?.id ?? null);
  const visibleRuns = selectedScope === 'team' ? teamRuns : mineRuns;
  const selectedActiveExecutionId =
    selectedRun && isActiveRunStatus(selectedRun.status) ? selectedRun.id : null;
  const selectedActiveExecutionStatus =
    selectedRun && isActiveRunStatus(selectedRun.status) ? selectedRun.status : null;
  const runtimeActiveExecutionId =
    toolRuntimeState?.status === 'running' || toolRuntimeState?.status === 'blocked'
      ? toolRuntimeState.executionId
      : null;
  const runtimeActiveExecutionStatus =
    toolRuntimeState?.status === 'running' || toolRuntimeState?.status === 'blocked'
      ? (toolRuntimeState.executionStatus as FlowExecutionStatus)
      : null;
  const activeExecutionId = selectedActiveExecutionId ?? runtimeActiveExecutionId;
  const activeExecutionStatus =
    selectedActiveExecutionStatus ?? runtimeActiveExecutionStatus;
  const activeRun = useMemo(
    () =>
      activeExecutionId
        ? (allRuns.find((run) => run.id === activeExecutionId) ?? null)
        : null,
    [activeExecutionId, allRuns]
  );
  // A run waiting for approval can stay paused for days: Realtime is enough there.
  const hasRunningRunInList = useMemo(
    () => allRuns.some((run) => run.status === 'pending' || run.status === 'running'),
    [allRuns]
  );

  useEffect(() => {
    if (workspaceTabs.some((tab) => tab.id === activeWorkspaceTab)) return;
    // A campaign's address opens the campaigns page rather than the first one.
    const asked = askedCampaign && campaignsPageId ? workspaceTabs.find((tab) => tab.id === `page:${campaignsPageId}`) : undefined;
    setActiveWorkspaceTab(asked?.id ?? workspaceTabs[0]?.id ?? 'tasks');
  }, [activeWorkspaceTab, workspaceTabs, askedCampaign, campaignsPageId]);

  const dashboardExecutionId =
    selectedRun?.status === 'completed' ? selectedRun.id : null;
  const attachedExecutionId = selectedExecutionId ?? activeExecutionId;
  const selectedRunLabel = selectedRun
    ? `${selectedRun.displayName ?? 'Équipe'} • ${formatRunTimestamp(getRunTimestamp(selectedRun))}`
    : null;
  const attachedExecutionLabel =
    selectedRunLabel ??
    (activeRun
      ? `${activeRun.displayName ?? 'Moi'} • ${formatRunTimestamp(getRunTimestamp(activeRun))}`
      : null);
  const resultPanelDescription =
    selectedRun?.status === 'completed'
      ? `Résultat à onglets · ${getVisibleRunTitle(selectedRun.title)} · ${formatRunTimestamp(getRunTimestamp(selectedRun))}`
      : selectedRun && isActiveRunStatus(selectedRun.status)
        ? 'Cette tâche est en cours ou en attente. Le dernier résultat terminé reste accessible pendant qu’elle avance.'
        : selectedRun?.status === 'failed'
          ? 'Cette tâche a échoué. Elle reste visible pour diagnostic, sans remplacer le dernier résultat terminé.'
          : selectedRun?.status === 'cancelled'
            ? 'Cette tâche a été annulée. Le dernier résultat terminé reste affiché ici.'
            : runtimeActiveExecutionId
              ? 'Une tâche avance en arrière-plan. Le dernier résultat terminé reste affiché ici pendant ce temps.'
              : 'Le dernier résultat personnel s’affiche ici par défaut quand il existe.';
  const executionTraceContextSummary = useMemo(() => {
    if (!attachedExecutionId) return null;

    const stepLabels = executionTrace.steps.slice(0, 5).map((step) => {
      const label =
        executionTrace.snapshotDominos.find((domino) => domino.position === step.position)
          ?.label ??
        executionTrace.snapshotDominos[step.position - 1]?.label ??
        step.action_type;
      return `${step.position}. ${label} (${step.status})`;
    });
    const messageSnippets = executionTrace.messages
      .map(getExecutionTraceMessageText)
      .filter((text): text is string => Boolean(text))
      .slice(-3)
      .map((text) => clampText(text, 220));

    const parts = [
      `Exécution sélectionnée : ${attachedExecutionId}.`,
      stepLabels.length > 0 ? `Étapes visibles : ${stepLabels.join(' · ')}.` : null,
      messageSnippets.length > 0
        ? `Dernières synthèses d’exécution : ${messageSnippets.join(' ')}`
        : null,
    ].filter((value): value is string => Boolean(value));

    return parts.length > 0 ? clampText(parts.join(' '), 800) : null;
  }, [
    attachedExecutionId,
    executionTrace.messages,
    executionTrace.snapshotDominos,
    executionTrace.steps,
  ]);
  const assistantContext = useMemo(() => {
    if (!toolRef || !toolMeta) return null;

    const recentActivitySummary = selectedExecutionActivitySummary
      ? selectedExecutionActivitySummary.items
          .map((item) => item.outcomeSummary)
          .filter((summary): summary is string => Boolean(summary))
          .slice(0, 2)
          .join(' ')
      : null;

    const visibleSummary = clampText(
      [
        toolMeta.promise,
        resultPanelDescription,
        attachedExecutionLabel ? `Tâche courante : ${attachedExecutionLabel}.` : null,
        userFlowId ? `Tool créé par l’utilisateur : ${userFlowId}.` : null,
        executionTraceContextSummary,
        recentActivitySummary,
        threadContext?.threadState?.currentTopic
          ? `Thread courant : ${threadContext.threadState.currentTopic.title} (${threadContext.threadState.currentTopic.visibleState}), topic ${threadContext.threadState.currentTopic.id}, version ${threadContext.threadState.currentTopic.version}.`
          : null,
      ]
        .filter(Boolean)
        .join(' '),
      MAX_ASSISTANT_VISIBLE_SUMMARY_LENGTH
    );

    return {
      surface: 'tool' as const,
      routePath: location.pathname,
      routeLabel: toolMeta.title,
      visibleSummary,
      visibleEntities: [
        toolMeta.title,
        selectedRun?.title,
        selectedExecutionActivitySummary?.subjectLabel,
      ].filter((value): value is string => Boolean(value)),
      selectedFlowId: userFlowId ?? null,
      dashboardId: dashboard?.id ?? null,
      toolKey: prebuiltToolKey,
      toolTitle: toolMeta.title,
      executionId: attachedExecutionId ?? null,
      executionLabel: attachedExecutionLabel ?? null,
      // P3 (04/11): the list on screen is read by the chat without being searched for.
      appList: app ? { appId: app.id } : null,
    };
  }, [
    app,
    attachedExecutionId,
    attachedExecutionLabel,
    dashboard?.id,
    executionTraceContextSummary,
    resultPanelDescription,
    selectedExecutionActivitySummary,
    selectedRun?.title,
    threadContext,
    prebuiltToolKey,
    toolMeta,
    toolRef,
    userFlowId,
    location.pathname,
  ]);

  useRegisterAppAssistantContext(recordId ? objectContext : assistantContext);
  const {
    blockedStep,
    isLoading: executionChatLoading,
    isSubmitting: executionChatSubmitting,
    error: executionChatError,
    runAct,
  } = useExecutionChatState(activeExecutionId, activeExecutionStatus);

  const updateSearch = (mutate: (next: URLSearchParams) => void) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        mutate(next);
        return next;
      },
      { replace: true, state: location.state }
    );
  };

  useEffect(() => {
    if (!toolMeta) {
      setToolFormValues({});
      return;
    }

    setToolFormValues({
      ...toolMeta.defaultValues,
      ...toolPrefill,
    });
  }, [toolMeta, toolPrefill]);

  const setSelectedRun = (runId: string | null, scope: 'mine' | 'team') => {
    updateSearch((next) => {
      next.set('scope', scope);
      if (runId) {
        next.set('run', runId);
      } else {
        next.delete('run');
      }
    });
  };

  const focusResultSurface = () => {
    window.requestAnimationFrame(() => {
      document
        .getElementById('tool-result-surface')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleSelectRun = (runId: string | null, scope: 'mine' | 'team') => {
    setSelectedRun(runId, scope);
    setActiveWorkspaceTab('tasks');
    if (runId) focusResultSurface();
  };

  const handleOpenRun = (run: ToolRunListItem, scope: 'mine' | 'team') => {
    handleSelectRun(run.id, scope);
  };

  const refreshRunLists = useCallback(
    async (params: { flowId: string; userId: string; silent?: boolean }) => {
      const requestId = ++runListRequestIdRef.current;
      if (!params.silent) setRunsLoading(true);
      try {
        const [mine, team] = await Promise.all([
          listToolRuns({
            flowId: params.flowId,
            currentUserId: params.userId,
            scope: 'mine',
          }),
          listToolRuns({
            flowId: params.flowId,
            currentUserId: params.userId,
            scope: 'team',
          }),
        ]);

        if (selectedExecutionId && ![...mine, ...team].some(run => run.id === selectedExecutionId)) {
          const selectedMine = await listToolRuns({
            flowId: params.flowId, currentUserId: params.userId,
            scope: 'mine', runId: selectedExecutionId, limit: 1,
          });
          if (selectedMine.length > 0) mine.push(...selectedMine);
          else team.push(...await listToolRuns({
            flowId: params.flowId, currentUserId: params.userId,
            scope: 'team', runId: selectedExecutionId, limit: 1,
          }));
        }

        if (requestId === runListRequestIdRef.current) {
          setMineRuns(mine);
          setTeamRuns(team);
        }
      } finally {
        if (requestId === runListRequestIdRef.current) setRunsLoading(false);
      }
    },
    [selectedExecutionId]
  );

  useEffect(() => {
    const flowId = toolRuntimeState?.flowId;
    if (!flowId || !currentUserId) return;

    let active = true;
    let timeoutId: number | null = null;

    const scheduleRefresh = () => {
      if (timeoutId !== null) return;
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        if (!active) return;
        void refreshRunLists({
          flowId,
          userId: currentUserId,
          silent: true,
        });
      }, 150);
    };

    const channel = supabase
      // Unique per subscription: realtime-js returns a same-named channel that is
      // still leaving, and the subscription then dies silently (audit B7).
      .channel(`tool-runs-${flowId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'flow_executions',
          filter: `flow_id=eq.${flowId}`,
        },
        () => {
          if (!active) return;
          scheduleRefresh();
        }
      )
      .subscribe((status) => {
        if (!active) return;
        if (status === 'SUBSCRIBED') scheduleRefresh();
      });

    return () => {
      active = false;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      void supabase.removeChannel(channel);
    };
  }, [currentUserId, refreshRunLists, toolRuntimeState?.flowId]);

  useEffect(() => {
    const flowId = toolRuntimeState?.flowId;
    if (!flowId || !currentUserId) return;
    if (!hasRunningRunInList && runtimeStatus !== 'running' && runtimeStatus !== 'blocked')
      return;

    const intervalId = window.setInterval(() => {
      void refreshRunLists({
        flowId,
        userId: currentUserId,
        silent: true,
      });
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [
    currentUserId,
    hasRunningRunInList,
    refreshRunLists,
    runtimeStatus,
    toolRuntimeState?.flowId,
  ]);

  const refreshRuntime = async (options?: { submission?: ToolPageSubmission | null }) => {
    if (!toolRef) return;

    if (options?.submission) {
      setRuntimeSubmitting(true);
    } else {
      setRuntimeLoading(true);
    }
    setError(null);

    try {
      if (prebuiltToolKey && bootstrappedToolKeyRef.current !== prebuiltToolKey) {
        await bootstrapPrebuiltTools();
        bootstrappedToolKeyRef.current = prebuiltToolKey;
      }

      const runtimeTarget = prebuiltToolKey
        ? { toolKey: prebuiltToolKey }
        : userFlowId
          ? { flowId: userFlowId }
          : null;
      if (!runtimeTarget) return;

      const runtime = await resolveToolRuntime({
        ...runtimeTarget,
        submission: options?.submission ?? null,
        clientContext: {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
        },
        handoffOrigin: options?.submission ? toolHandoffOrigin : null,
        proposalHandoff: options?.submission ? threadProposalHandoff : null,
        launchContext: options?.submission ? toolLaunchContext : null,
      });

      setToolRuntimeState(runtime);
      if (
        options?.submission &&
        (runtime.status === 'running' || runtime.status === 'blocked')
      ) {
        setSelectedRun(runtime.executionId, 'mine');
      }

      if (runtime.dashboardId) {
        const dashboardRow = await loadDashboardById(runtime.dashboardId);
        setDashboard(dashboardRow);
      } else {
        setDashboard(null);
        setComposition(null);
        setViewEnvelopesByInstanceId({});
        setSelectedExecutionActivitySummary(null);
      }

      if (runtime.flowId && currentUserId) {
        await refreshRunLists({
          flowId: runtime.flowId,
          userId: currentUserId,
        });
      } else {
        setMineRuns([]);
        setTeamRuns([]);
      }
    } catch (runtimeError) {
      setError(
        await toDashboardUiError(
          runtimeError,
          'Impossible de préparer ce tool pour le moment.'
        )
      );
    } finally {
      setRuntimeLoading(false);
      setRuntimeSubmitting(false);
    }
  };

  useEffect(() => {
    if (!userFlowId) {
      setUserFlow(null);
      setUserFlowLoading(false);
      return;
    }

    let active = true;

    const loadUserFlow = async () => {
      setUserFlowLoading(true);
      setError(null);

      try {
        const { data, error: flowError } = await supabase
          .from('flows')
          .select('*')
          .eq('id', userFlowId)
          // An archived app still opens to read its work; it is not launched.
          .maybeSingle();

        if (flowError && flowError.code !== 'PGRST116') {
          throw flowError;
        }

        if (!active) return;
        setUserFlow((data as Flow | null) ?? null);
      } catch (loadError) {
        if (!active) return;
        setUserFlow(null);
        setError(
          await toDashboardUiError(
            loadError,
            'Impossible de charger ce tool pour le moment.'
          )
        );
      } finally {
        if (active) setUserFlowLoading(false);
      }
    };

    void loadUserFlow();

    return () => {
      active = false;
    };
  }, [userFlowId]);

  useEffect(() => {
    if (!toolRef) return;

    let active = true;

    const loadUserContext = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: authData, error: authError } = await supabase.auth.getUser();
        if (authError || !authData.user) {
          throw authError ?? new Error('Utilisateur non authentifié');
        }

        if (!active) return;
        setCurrentUserId(authData.user.id);

        const { data: tenantMember, error: tenantMemberError } = await supabase
          .from('tenant_members')
          .select('tenant_role')
          .eq('user_id', authData.user.id)
          .maybeSingle();

        if (tenantMemberError && tenantMemberError.code !== 'PGRST116') {
          throw tenantMemberError;
        }

        if (!active) return;
        setIsTenantManager(tenantMember?.tenant_role === 'manager');
      } catch (loadError) {
        if (!active) return;
        setError(
          await toDashboardUiError(
            loadError,
            'Impossible de charger ce tool pour le moment.'
          )
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadUserContext();

    return () => {
      active = false;
    };
  }, [toolRef]);

  useEffect(() => {
    if (!toolRef || !currentUserId) return;
    void refreshRuntime();
  }, [toolRef, currentUserId]);

  useEffect(() => {
    void loadThreadContext();
  }, [loadThreadContext]);

  useEffect(() => {
    if (!dashboard) return;

    let active = true;

    const hydrateSelectedRun = async () => {
      try {
        const execution = await invokeDashboardExecute({
          dashboardId: dashboard.id,
          selectedExecutionId: dashboardExecutionId,
        });

        if (!active) return;
        setComposition(execution.composition);
        setViewEnvelopesByInstanceId(execution.viewEnvelopesByInstanceId);
        setSelectedExecutionActivitySummary(
          execution.selectedExecutionActivitySummary ?? null
        );
      } catch (dashboardError) {
        if (!active) return;
        setError(
          await toDashboardUiError(
            dashboardError,
            'Impossible d’afficher le résultat du tool pour le moment.'
          )
        );
      }
    };

    void hydrateSelectedRun();

    return () => {
      active = false;
    };
  }, [dashboard?.id, dashboardExecutionId]);

  // The run lists are rebuilt on every poll: reload a result only when a run
  // appears or changes status, never because a new array arrived (audit B7).
  const runsForResultRef = useRef({ mineRuns, teamRuns, selectedRun });
  runsForResultRef.current = { mineRuns, teamRuns, selectedRun };
  const runStatusesKey = useMemo(
    () => [...mineRuns, ...teamRuns].map((run) => `${run.id}:${run.status}`).join('|'),
    [mineRuns, teamRuns]
  );
  const selectedRunId = selectedRun?.id ?? null;
  const selectedRunStatus = selectedRun?.status ?? null;

  useEffect(() => {
    const { selectedRun, mineRuns, teamRuns } = runsForResultRef.current;
    if (!toolRuntimeState?.flowId || !selectedRun || selectedRun.status !== 'completed') {
      setFlowResultTabs([]);
      setActiveFlowResultTabId(null);
      setFlowResultSource(null);
      setRunResultScope(null);
      return;
    }

    let active = true;

    const hydrateFlowResult = async () => {
      try {
        const { data, error: executionError } = await supabase
          .from('flow_executions')
          .select(
            'id, flow_id, user_id, result_scope, view_payload, result_payload, view_type_resolved, view_type_requested, execution_summary'
          )
          .eq('id', selectedRun.id)
          .eq('flow_id', toolRuntimeState.flowId)
          .single();

        if (executionError || !data) {
          throw executionError ?? new Error('Résultat introuvable.');
        }

        if (!active) return;

        const viewPayload = (data as { view_payload: unknown }).view_payload;
        const resultPayload = parseResultPayload(
          (data as { result_payload: unknown }).result_payload
        );
        if (data.result_payload != null && resultPayload === null) throw new Error('Le résultat enregistré est illisible.');
        const row = data as {
          view_type_resolved: unknown;
          view_type_requested: unknown;
          execution_summary: unknown;
        };
        const fallback = isAnyViewEnvelope(viewPayload)
          ? {
              viewType: (isViewType(row.view_type_resolved)
                ? row.view_type_resolved
                : isViewType(row.view_type_requested)
                  ? row.view_type_requested
                  : viewPayload.view_type) as ViewType,
              envelope: viewPayload,
            }
          : null;

        const tabs = buildRunResultTabs({
          title: toolMeta?.title ?? getVisibleRunTitle(selectedRun.title),
          description:
            getExecutionSummaryNarration(row.execution_summary) ??
            selectedRun.summary ??
            toolMeta?.description ??
            null,
          resultPayload,
          fallback,
          aggregateRuns: [...mineRuns, ...teamRuns],
        });

        setFlowResultTabs(tabs);
        const requestedTabId = searchParams.get('outputTab');
        setActiveFlowResultTabId((current) =>
          tabs.find((tab) => tab.id === requestedTabId)?.id
          ?? tabs.find((tab) => tab.id === current)?.id
          ?? tabs[0]?.id
          ?? null);
        setFlowResultSource({
          runId: selectedRun.id,
          composite: resultPayload !== null,
        });
        const scopeRaw = (data as { result_scope?: unknown }).result_scope;
        const isOwnRun = (data as { user_id?: unknown }).user_id === currentUserId;
        setRunResultScope(
          isOwnRun &&
            (scopeRaw === 'mine' || scopeRaw === 'team' || scopeRaw === 'tenant')
            ? scopeRaw
            : null
        );
      } catch (loadError) {
        if (!active) return;
        setFlowResultTabs([]);
        setActiveFlowResultTabId(null);
        setFlowResultSource(null);
        setRunResultScope(null);
        setError(
          await toDashboardUiError(
            loadError,
            'Impossible d’afficher le résultat du tool pour le moment.'
          )
        );
      }
    };

    void hydrateFlowResult();

    return () => {
      active = false;
    };
  }, [
    currentUserId,
    runStatusesKey,
    selectedRunId,
    selectedRunStatus,
    toolMeta?.description,
    toolMeta?.title,
    toolRuntimeState?.flowId,
  ]);

  useEffect(() => {
    if (activeExecutionStatus === 'paused') {
      setActionSheetDismissed(false);
    }
  }, [activeExecutionId, activeExecutionStatus]);

  const toolLaunchRequested = resolveToolLaunchRequested(location.state);
  useEffect(() => {
    if (Object.keys(toolPrefill).length > 0 || toolLaunchRequested) {
      setComposerOpen(true);
    }
  }, [toolPrefill, toolLaunchRequested]);

  useEffect(() => {
    if (
      (runtimeStatus !== 'running' && runtimeStatus !== 'blocked') ||
      runtimeLoading ||
      runtimeSubmitting
    )
      return;

    const timeoutId = window.setTimeout(() => {
      void refreshRuntime();
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [activeExecutionId, runtimeLoading, runtimeSubmitting, runtimeStatus]);

  if (toolRef && (loading || userFlowLoading)) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <Card className="rounded-[28px] border-domino-cardBorder bg-domino-warm">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink">
            Préparation de la page outil
          </h1>
          <p className="mt-3 text-sm leading-6 text-domino-inkSoft">
            Domino charge l’application et son résultat.
          </p>
        </Card>
      </div>
    );
  }

  if (!toolRef || !toolMeta) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <Card className="rounded-[28px] border-domino-cardBorder bg-domino-warm">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink">
            Outil introuvable
          </h1>
          <p className="mt-3 text-sm leading-6 text-domino-inkSoft">
            Cet outil n est pas encore disponible.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/tools')}>Retour aux outils</Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleToolSubmit = async () => {
    if (!toolRef || !toolMeta) return;

    if (
      toolRuntimeState?.status === 'missing_one_thing' &&
      toolRuntimeState.requirement.isConnector
    ) {
      navigate('/connections');
      return;
    }

    if (prebuiltToolKey === 'analyze_call') {
      const transcript = (toolFormValues.transcript ?? '').trim();
      if (transcript.length === 0) return;

      setSelectedRun(null, 'mine');
      await refreshRuntime({
        submission: {
          kind: 'paste_transcript',
          transcript,
        },
      });
      setToolFormValues({
        ...toolMeta.defaultValues,
      });
      setComposerOpen(false);
      return;
    }

    const normalizedValues = Object.entries(toolFormValues).reduce<
      Record<string, string>
    >((accumulator, [key, value]) => {
      accumulator[key] = value.trim();
      return accumulator;
    }, {});

    const missingRequiredField = toolMeta.fields.some(
      (field) => field.required && !(normalizedValues[field.key] ?? '')
    );

    if (missingRequiredField) {
      return;
    }

    setSelectedRun(null, 'mine');
    await refreshRuntime({
      submission: {
        kind: 'provide_inputs',
        inputValues: normalizedValues,
      },
    });
    updateSearch((next) => {
      next.delete('prefill');
      next.delete('prefillKey');
      next.delete('origin');
    });
    setComposerOpen(false);
  };
  const handleBlockedSubmit = async (answer: unknown): Promise<void> => {
    await runAct(
      'submit_hitl',
      { answer },
      {
        clientMessage: 'Réponse HITL envoyée',
      }
    );
    await refreshRuntime();
  };

  const handleCancelActiveExecution = async (): Promise<void> => {
    await runAct('cancel_execution', undefined, {
      clientMessage: 'Annuler la tâche',
    });
    await refreshRuntime();
  };

  const handleProductDiagnosisAction = (action: ProductDiagnosisAction) => {
    if (action.type === 'connect_source') {
      navigate('/connections');
      return;
    }

    if (action.type === 'provide_input' || action.type === 'retry') {
      setComposerOpen(true);
      return;
    }

    if (action.type === 'contact_support') {
      window.location.href = buildSupportMailto({
        subject: 'Lancement arrêté',
        executionId: selectedRun?.id,
      });
      return;
    }

    if (action.type === 'edit_tool' || action.type === 'repair_tool') {
      const flowId = toolRuntimeState?.flowId;
      if (flowId) {
        const repairRunParam = selectedRun?.id
          ? `&repairRun=${encodeURIComponent(selectedRun.id)}`
          : '';
        // Repair is driven by the technical, planner-facing hint (it may name
        // primitives/fields). Fall back to user copy only for older runs that
        // predate the internal diagnosis.
        const repairPrompt =
          action.type === 'repair_tool'
            ? (selectedRun?.internalDiagnosis?.repairHint ??
              (selectedRun?.productDiagnosis
                ? [
                    selectedRun.productDiagnosis.title,
                    selectedRun.productDiagnosis.explanation,
                  ].join('\n')
                : null))
            : null;
        navigate(
          `/forge?flowId=${encodeURIComponent(flowId)}${repairRunParam}`,
          repairPrompt ? { state: { repairPrompt } } : undefined
        );
        return;
      }
    }

    if (selectedRun) {
      navigate(`/executions/${selectedRun.id}`);
    }
  };

  const errorNotice = error && (
    <Card className="border-red-200 bg-red-50">
      <p className="text-sm text-red-700">{error.message}</p>
    </Card>
  );

  const missingRequirement =
    toolRuntimeState?.status === 'missing_one_thing'
      ? toolRuntimeState.requirement
      : null;
  const isConnectorRequirement = missingRequirement?.isConnector === true;
  const isSubmitDisabled =
    !isConnectorRequirement &&
    toolMeta.fields.some(
      (field) => field.required && !(toolFormValues[field.key] ?? '').trim()
    );
  const shouldShowComposer = composerOpen;
  const shouldShowActionSheet =
    activeExecutionStatus === 'paused' && !actionSheetDismissed;
  const shouldShowActionSheetBanner =
    activeExecutionStatus === 'paused' && actionSheetDismissed;
  const shouldShowRunningRunBanner =
    activeExecutionStatus === 'running' || activeExecutionStatus === 'pending';
  const hasCurrentFlowResult = flowResultSource?.runId === selectedRun?.id;
  const hasCompositeRunResult = hasCurrentFlowResult && flowResultSource?.composite === true;
  const resultSurfaceTabs: ToolResultSurfaceTab[] = (() => {
    const tabs: ToolResultSurfaceTab[] = [];

    if (dashboard && selectedRun && composition && !hasCompositeRunResult) {
      tabs.push({
        id: DASHBOARD_RESULT_TAB_ID,
        label: 'Résultat',
        kind: 'result',
        scope: 'run',
      });
    }

    if ((!dashboard && hasCurrentFlowResult) || hasCompositeRunResult) {
      tabs.push(
        ...flowResultTabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
          kind: 'result' as const,
          scope: tab.scope,
        }))
      );
    }

    if (selectedRun) {
      tabs.push({
        id: EXECUTION_TRACE_TAB_ID,
        label: 'Exécution',
        kind: 'execution',
        scope: 'run',
      });
    }

    return tabs;
  })();
  const activeResultSurfaceTab =
    resultSurfaceTabs.find((tab) => tab.id === (readDocumentOutput(searchParams)?.tabId ?? activeFlowResultTabId)) ??
    resultSurfaceTabs[0] ??
    null;
  const isExecutionTraceTabActive = activeResultSurfaceTab?.kind === 'execution';
  const usesFlowResult = (!dashboard && hasCurrentFlowResult) || hasCompositeRunResult;
  const activeFlowResultTab =
    usesFlowResult && activeResultSurfaceTab?.kind === 'result'
      ? (flowResultTabs.find((tab) => tab.id === activeResultSurfaceTab.id) ?? null)
      : null;
  const resultComposition = usesFlowResult
    ? (activeFlowResultTab?.composition ?? null)
    : composition;
  const resultViewEnvelopesByInstanceId = usesFlowResult
    ? (activeFlowResultTab?.viewEnvelopesByInstanceId ?? {})
    : viewEnvelopesByInstanceId;
  const activeTabLabel =
    workspaceTabs.find((tab) => tab.id === 'tasks')?.label ?? 'Tâches';
  const teamWorkspaceEnabled = Boolean(prebuiltToolKey) || Boolean(userFlow && getUserFlowWorkspaceTabs(userFlow).some((tab) => tab.id === 'team'));

  const handleResultScopeChange = async (nextScope: RunResultScope) => {
    if (!selectedRun || runResultScope === nextScope) return;
    const previous = runResultScope;
    setRunScopeSaving(true);
    setRunResultScope(nextScope);
    try {
      await updateRunResultScope({
        executionId: selectedRun.id,
        scope: nextScope,
      });
    } catch (scopeError) {
      setRunResultScope(previous);
      setError(
        await toDashboardUiError(
          scopeError,
          'Impossible de modifier le partage de ce résultat.'
        )
      );
    } finally {
      setRunScopeSaving(false);
    }
  };

  const renderProductDiagnosisCard = (run: ToolRunListItem) => {
    const diagnosis = run.productDiagnosis;
    if (!diagnosis) return null;

    const secondaryActions = diagnosis.secondaryActions.filter(
      (action) => action.type !== diagnosis.primaryAction.type
    );

    return (
      <div className="rounded-[24px] border border-red-100 bg-red-50 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-domino-white text-red-700">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-domino-ink">
                {diagnosis.title}
              </h3>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-domino-inkSoft">
                {diagnosis.explanation}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            <Button
              onClick={() => handleProductDiagnosisAction(diagnosis.primaryAction)}
              className="max-w-[220px] whitespace-normal text-left"
            >
              {diagnosis.primaryAction.label}
            </Button>
            {secondaryActions.map((action) => (
              <Button
                key={`${action.type}:${action.label}`}
                variant="secondary"
                onClick={() => handleProductDiagnosisAction(action)}
                className="max-w-[220px] whitespace-normal text-left"
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderRunRow = (run: ToolRunListItem, scope: 'mine' | 'team') => {
    const selected = run.id === selectedExecutionId;
    const statusMeta = getRunStatusMeta(run.status);
    const actionLabel = isActiveRunStatus(run.status) ? 'Suivre' : 'Voir';
    const visibleTitle = getVisibleRunTitle(run.title);
    const visibleSummary = getVisibleRunSummary(run);

    return (
      <tr
        tabIndex={0}
        onKeyDown={(event) => { if (event.key === "Enter") handleOpenRun(run, scope); }}
        key={run.id}
        onClick={() => handleOpenRun(run, scope)}
        className={cn(
          'cursor-pointer border-b border-domino-cardBorder transition-colors last:border-b-0',
          selected ? 'bg-domino-lilac' : 'hover:bg-domino-muted'
        )}
      >
        <td className="w-[44%] px-5 py-4 align-middle">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-domino-lilac text-domino-aubergine">
              <ClipboardList className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-domino-ink">
                {visibleTitle}
              </p>
              {visibleSummary && (
                <p className="mt-1 line-clamp-1 text-xs text-domino-inkSoft">
                  {visibleSummary}
                </p>
              )}
            </div>
          </div>
        </td>
        <td className="px-5 py-4 align-middle text-sm font-medium text-domino-ink">
          {run.userId === currentUserId ? 'Vous' : run.displayName ?? 'Membre sans nom'}
        </td>
        <td className="px-5 py-4 align-middle">
          <span className={cn('inline-flex text-xs font-medium', statusMeta.className)}>
            {statusMeta.label}
          </span>
        </td>
        <td className="px-5 py-4 align-middle text-sm text-domino-inkSoft">
          {formatRunTimestamp(getRunTimestamp(run))}
        </td>
        <td className="px-5 py-4 text-right align-middle">
          <span className="ml-auto inline-flex items-center justify-end gap-1 text-xs font-semibold text-domino-inkSoft">
            {actionLabel}
            <ChevronRight className="h-4 w-4" />
          </span>
        </td>
      </tr>
    );
  };

  const renderRunsTable = (runs: ToolRunListItem[], scope: 'mine' | 'team') => (
    <div className="overflow-hidden rounded-[26px] border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-soft)]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] table-fixed">
          <thead className="bg-domino-muted">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-domino-inkSoft">
              <th className="w-[44%] px-5 py-3">Résultat</th>
              <th className="px-5 py-3">Propriétaire</th>
              <th className="px-5 py-3">Statut</th>
              <th className="px-5 py-3">Date</th>
              <th className="w-16 px-5 py-3" />
            </tr>
          </thead>
          <tbody>{runs.map((run) => renderRunRow(run, scope))}</tbody>
        </table>
      </div>
    </div>
  );

  const renderEmptyState = (label: string) => (
    <div className="rounded-[26px] border border-dashed border-domino-cardBorder bg-domino-white px-6 py-10 text-center">
      <p className="text-sm font-semibold text-domino-ink">{label}</p>
    </div>
  );

  const libraryRuns = visibleRuns.filter((run) => run.status === 'completed');
  const showRunDetail = activeWorkspaceTab === 'tasks' && selectedRun !== null;
  const launchLabel = prebuiltToolKey === 'analyze_call' ? 'Analyser un appel' : prebuiltToolKey ? toolMeta.launchLabel : 'Nouvelle utilisation';
  // An app filled from a table (« Prospection », 03/11) imports instead of launching a tool.
  const startsWithImport = currentAppSpec.start === 'import' && Boolean(app);
  const openStart = () => setComposerOpen(true);

  return (
    <div className="min-h-full bg-[linear-gradient(180deg,#FDFBF7_0%,#FAF7F0_100%)]">
      <header className="border-b border-domino-cardBorder bg-domino-warm">
        <div className="mx-auto max-w-[1500px] px-5 py-4 lg:px-8">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-sm font-medium text-domino-inkSoft transition hover:text-domino-aubergine"
          >
            <ArrowLeft className="h-4 w-4" />
            Applications
          </Link>

          <div className="mt-3 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink">
                {showRunDetail && selectedRun ? getVisibleRunTitle(selectedRun.title) : toolMeta.title}
                {!showRunDetail && app?.status === 'draft' && <span className="ml-3 text-sm font-normal text-domino-inkSoft">{APP_STATUS_LABELS.draft}</span>}
              </h1>
              {!showRunDetail && <p className="mt-1 max-w-3xl text-sm leading-6 text-domino-inkSoft">{toolMeta.promise}</p>}
              {showRunDetail && selectedRun && !isRedundantVisibleLabel(getVisibleRunTitle(selectedRun.title), toolMeta.title) && <button type="button" onClick={() => setSelectedRun(null, selectedScope)} className="mt-1 text-sm text-domino-aubergine">{toolMeta.title}</button>}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {app?.status === 'archived'
                ? <span className="px-2 text-sm text-domino-inkSoft">{APP_STATUS_LABELS.archived}</span>
                : startsWithImport ? <ImportMenu primary onChoose={setImportSource} /> : (
                  <Button
                    variant={showRunDetail ? "secondary" : "primary"}
                    className="shrink-0 gap-2"
                    onClick={openStart}
                  >
                    <Play className="h-4 w-4 fill-current" />
                    {launchLabel}
                  </Button>
                )}
              {app && <AppPageManageMenu app={app} onChanged={(next) => next ? setApp(next) : navigate('/tools')} />}
            </div>
          </div>

          {workspaceTabs.length > 1 && (
            <nav className="mt-6 flex gap-6 overflow-x-auto">
              {workspaceTabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeWorkspaceTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveWorkspaceTab(tab.id)}
                    className={cn(
                      'inline-flex shrink-0 items-center gap-2 border-b-2 px-1 pb-4 text-base font-semibold transition-colors',
                      active
                        ? 'border-domino-coral text-domino-ink'
                        : 'border-transparent text-domino-inkSoft hover:text-domino-ink'
                    )}
                  >
                    <Icon className={cn('h-4 w-4', active && 'text-domino-coral')} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </header>

      {app && recordId && (
        <AppObjectDrawer app={app} recordId={recordId} canManage={app.ownerUserId === currentUserId || isTenantManager}
          listColumnKeys={appPages.flatMap((page) => page.blocks).find((block) => block.type === 'records')?.columns}
          onAssistantContext={setObjectContext} />
      )}
      <WorkActionsProvider actionsElsewhere={showRunDetail} actions={threadContext ? getSubjectWorkActions(threadContext) : []} sourceRunId={selectedRun?.id} resultSource={selectedRun && ((usesFlowResult && activeFlowResultTab?.documentOutputs !== undefined) || (Object.values(resultViewEnvelopesByInstanceId).length === 1 && Object.values(resultViewEnvelopesByInstanceId)[0]?.view_type === 'artifact_document')) ? { kind: 'flow', id: selectedRun.id } : undefined}>
      <main className="mx-auto max-w-[1500px] space-y-5 px-5 py-5 lg:px-8">
        {errorNotice}

        {threadContextEnabled && threadContext?.threadState && (
          <ToolThreadContextCard
            detail={threadContext}
            threadState={threadContext.threadState}
            focusedTopicId={threadProposalHandoff?.topicId ?? null}
            focusedFlowRunId={showRunDetail ? selectedRun?.id : null}
            focusedVersion={showRunDetail ? searchParams.get('version') : null}
            focusedOutput={showRunDetail ? readDocumentOutput(searchParams) : null}
          />
        )}

        {showRunDetail && threadContext && (() => {
          const [primary,...others] = getSubjectWorkActions(threadContext).filter(work => work.context.sourceRunId === selectedRun?.id);
          if (!primary) return null;
          return <section className="border-b border-domino-cardBorder pb-5"><WorkActionCard work={primary} />
            {others.length > 0 && <details className="mt-5"><summary className="cursor-pointer text-sm font-medium">Autres actions sur ce compte</summary>{others.map(work => <div key={work.id} className="mt-4 border-t border-domino-cardBorder pt-4"><WorkActionCard work={work} compact /></div>)}</details>}
          </section>;
        })()}

        {app && (
          // Every page stays mounted: the list reports whether it exists before its tab is shown.
          appPages.filter((page) => !page.blocks.some((block) => block.type === 'runs')).map((page) => (
            <section key={page.id} aria-label={page.label} className={activeWorkspaceTab === `page:${page.id}` ? 'mx-auto max-w-[1500px] px-5 py-6 lg:px-8' : 'hidden'}>
              <AppSpecPage appId={app.id} appKey={app.key} canManage={app.ownerUserId === currentUserId || isTenantManager} page={page} importInHeader={startsWithImport}
                onAvailability={reportAppList} flowId={app.flowId}
                emptyAction={app?.status === 'archived' ? undefined : startsWithImport ? <ImportMenu onChoose={setImportSource} /> : <Button variant="secondary" size="sm" className="gap-2" onClick={openStart}><Play className="h-3.5 w-3.5 fill-current" />{launchLabel}</Button>} />
            </section>
          ))
        )}
        {app && storedAppSpec && storedAppSpec.issues.length > 0 && (
          <p role="status" className="mx-auto max-w-[1500px] px-5 text-xs text-domino-inkSoft lg:px-8">
            Une partie de la description de l’application n’a pas pu être affichée ({storedAppSpec.issues.join(', ')}).
          </p>
        )}

        {activeWorkspaceTab === 'tasks' && !showRunDetail && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {teamWorkspaceEnabled ? (
                <div className="flex border-b border-domino-cardBorder">
                  {(['mine', 'team'] as const).map((scope) => (
                    <button
                      key={scope}
                      type="button"
                      onClick={() => setSelectedRun(null, scope)}
                      className={cn(
                        'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                        selectedScope === scope
                          ? 'border-domino-aubergine text-domino-ink'
                          : 'border-transparent text-domino-inkSoft hover:text-domino-ink'
                      )}
                    >
                      {scope === 'mine' ? 'Mes résultats' : 'Équipe'}
                    </button>
                  ))}
                </div>
              ) : (
                <h2 className="text-xl font-semibold tracking-tight text-domino-ink">
                  {activeTabLabel}
                </h2>
              )}
              <Button
                variant="secondary"
                onClick={() => void refreshRuntime()}
                loading={runtimeLoading || runsLoading}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Actualiser
              </Button>
            </div>

            {visibleRuns.length === 0
              ? renderEmptyState('Aucun résultat pour le moment')
              : renderRunsTable(visibleRuns, selectedScope)}
          </div>
        )}

        {showRunDetail && selectedRun && (
          <div id="tool-result-surface" className="scroll-mt-5 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSelectedRun(null, selectedScope)}
                className="inline-flex items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm font-semibold text-domino-ink transition hover:border-domino-prune hover:text-domino-aubergine"
              >
                <ArrowLeft className="h-4 w-4" />
                Tous les résultats
              </button>
              <p className="text-sm text-domino-inkSoft">
                {selectedRun.userId === currentUserId ? 'Vous' : selectedRun.displayName ?? 'Membre sans nom'} ·{' '}
                {formatRunTimestamp(getRunTimestamp(selectedRun))}
              </p>
            </div>

            {selectedRun.status !== 'completed' && (
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>

                  {selectedRun.summary && (
                    <p className="mt-2 max-w-4xl text-sm leading-6 text-domino-inkSoft">
                      {selectedRun.summary}
                    </p>
                  )}
                </div>
                <Button
                  variant="secondary"
                  onClick={() => void refreshRuntime()}
                  loading={runtimeLoading}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Actualiser
                </Button>
              </div>
            )}

            {shouldShowRunningRunBanner && activeExecutionId && (
              <div className="rounded-[24px] border border-domino-cardBorder bg-domino-lilac px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-domino-aubergine">
                      En cours · {activeRun?.title ?? 'Traitement'}
                    </span>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => void handleCancelActiveExecution()}
                    loading={executionChatSubmitting}
                    className="gap-2"
                  >
                    <CircleStop className="h-4 w-4" />
                    Annuler
                  </Button>
                </div>
              </div>
            )}

            {shouldShowActionSheet && blockedStep && (
              <ToolActionRequiredSheet
                toolTitle={toolMeta.title}
                blockedStep={blockedStep}
                isSubmitting={executionChatSubmitting}
                error={executionChatError}
                onSubmitAnswer={handleBlockedSubmit}
                onCancelExecution={handleCancelActiveExecution}
                onOpenDetails={() => {
                  if (!activeExecutionId) return;
                  navigate(`/executions/${activeExecutionId}`);
                }}
                onDismiss={() => setActionSheetDismissed(true)}
              />
            )}

            {shouldShowActionSheet && !blockedStep && (
              <div className="rounded-[24px] border border-amber-200 bg-[var(--domino-warning-bg)] px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-domino-ink">Action requise</p>
                  <Button
                    variant="secondary"
                    onClick={() => void refreshRuntime()}
                    loading={runtimeLoading || executionChatLoading}
                  >
                    Actualiser
                  </Button>
                </div>
              </div>
            )}

            {shouldShowActionSheetBanner && (
              <div className="rounded-[24px] border border-amber-200 bg-[var(--domino-warning-bg)] px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-domino-ink">
                    Domino attend votre réponse
                  </p>
                  <Button onClick={() => setActionSheetDismissed(false)}>
                    Reprendre
                  </Button>
                </div>
              </div>
            )}

            {selectedRun.status === 'failed' && renderProductDiagnosisCard(selectedRun)}

            <div className="overflow-hidden">
              <div className="min-h-[520px]">
                <div className="space-y-5">
                  {resultSurfaceTabs.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-domino-cardBorder">
                      <div
                        className="flex flex-wrap gap-1"
                        role="tablist"
                        aria-label="Perspectives du résultat"
                      >
                        {resultSurfaceTabs.map((tab) => (
                          <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={tab.id === activeResultSurfaceTab?.id}
                            className={cn(
                              'flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition',
                              tab.id === activeResultSurfaceTab?.id
                                ? 'border-domino-coral text-domino-ink'
                                : 'border-transparent text-domino-inkSoft hover:text-domino-ink'
                            )}
                            onClick={() => {
                              setActiveFlowResultTabId(tab.id);
                              const next = new URLSearchParams(searchParams);
                              next.delete('outputTab'); next.delete('outputSection'); next.delete('version');
                              setSearchParams(next, { replace: true });
                            }}
                          >
                            {tab.kind === 'result'
                              ? getResultTabLabel(toolMeta.title, tab.label)
                              : tab.label}
                            {tab.kind === 'result'
                              ? resultTabScopeSuffix(getResultTabLabel(toolMeta.title, tab.label), tab.scope)
                              : null}
                          </button>
                        ))}
                      </div>

                      {runResultScope !== null &&
                        activeResultSurfaceTab?.kind === 'result' && (
                          <label className="mb-2 inline-flex items-center gap-2 text-xs text-domino-inkSoft">
                            Partager
                            <select
                              aria-label="Partage du résultat"
                              value={runResultScope}
                              disabled={runScopeSaving}
                              onChange={(event) =>
                                void handleResultScopeChange(
                                  event.currentTarget.value as RunResultScope
                                )
                              }
                              className="border-0 bg-transparent py-1 pr-7 text-xs font-medium text-domino-ink focus:ring-0"
                            >
                              <option value="mine">Moi</option>
                              <option value="team">Équipe</option>
                              <option value="tenant">Entreprise</option>
                            </select>
                          </label>
                        )}
                    </div>
                  )}

                  {isExecutionTraceTabActive ? (
                    <ToolExecutionTracePanel
                      executionId={selectedRun.id}
                      executionStatus={selectedRun.status}
                      steps={executionTrace.steps}
                      messages={executionTrace.messages}
                      snapshotDominos={executionTrace.snapshotDominos}
                      launchInputs={executionTrace.launchInputs}
                      loading={executionTrace.loading}
                      error={executionTrace.error}
                    />
                  ) : !resultComposition ? (
                    <div className="mx-5 rounded-[24px] border border-dashed border-domino-cardBorder bg-domino-white px-6 py-10 lg:mx-6">
                      <h2 className="text-2xl font-semibold tracking-tight text-domino-ink">
                        {loading || runtimeLoading ? 'Chargement' : 'Aucun résultat'}
                      </h2>
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-domino-inkSoft">
                        Ouvre l’onglet Exécution pour suivre le travail et consulter les
                        synthèses d’étapes.
                      </p>
                    </div>
                  ) : (
                    <>
                      {!threadContext && selectedExecutionActivitySummary && (
                        <ToolRecentActivityCard
                          summary={selectedExecutionActivitySummary}
                        />
                      )}
                      <ToolResultPageRenderer
                        composition={resultComposition}
                        viewEnvelopesByInstanceId={resultViewEnvelopesByInstanceId}
                        documentOutputs={usesFlowResult ? activeFlowResultTab?.documentOutputs : undefined}
                        requestedOutput={readDocumentOutput(searchParams)}
                        pageMode={toolMeta.pageMode}
                        sectionOrder={toolMeta.sectionOrder}
                        pageTitle={getVisibleRunTitle(selectedRun.title)}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeWorkspaceTab === 'library' && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-2xl font-semibold tracking-tight text-domino-ink">
                {workspaceTabs.find((tab) => tab.id === 'library')?.label ??
                  'Bibliothèque'}
              </h2>
            </div>

            {libraryRuns.length === 0 ? (
              renderEmptyState('Aucun résultat')
            ) : (
              <div className="grid gap-4 lg:grid-cols-3">
                {libraryRuns.map((run) => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => handleSelectRun(run.id, selectedScope)}
                    className="min-h-[180px] rounded-[26px] border border-domino-cardBorder bg-domino-white px-5 py-5 text-left shadow-[var(--domino-shadow-soft)] transition hover:border-domino-prune hover:bg-domino-muted"
                  >
                    <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                    <p className="mt-5 text-lg font-semibold tracking-tight text-domino-ink">
                      {getVisibleRunTitle(run.title)}
                    </p>
                    <p className="mt-2 text-sm text-domino-inkSoft">
                      {formatRunTimestamp(getRunTimestamp(run))}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeWorkspaceTab === 'team' && (
          <div className="space-y-5">
            {teamRuns.length === 0
              ? renderEmptyState('Aucune tâche équipe')
              : renderRunsTable(teamRuns, 'team')}
          </div>
        )}
      </main>
      </WorkActionsProvider>

      <ToolLaunchDialog
        open={shouldShowComposer}
        toolTitle={toolMeta.title}
        contextLabel={threadContext?.subjectLabel ?? null}
        composerTitle={toolMeta.composerTitle}
        submitLabel={toolMeta.submitLabel}
        fields={toolMeta.fields}
        values={toolFormValues}
        missingRequirement={missingRequirement}
        hasActiveRun={Boolean(runtimeActiveExecutionId)}
        runtimeLoading={runtimeLoading}
        runtimeSubmitting={runtimeSubmitting}
        submitDisabled={isSubmitDisabled}
        onClose={() => setComposerOpen(false)}
        onSubmit={() => void handleToolSubmit()}
        onRefresh={() => void refreshRuntime()}
        onValueChange={(key, value) =>
          setToolFormValues((current) => ({
            ...current,
            [key]: value,
          }))
        }
      />
      {importSource && app && (
        <TableImportDrawer toolAppId={app.id} source={importSource} initialText={importText} initialApp={importApp}
          campaignName={importCampaignName} allowNoCampaign={Boolean(campaignsPageId)}
          initialCampaign={campaignsPageId ? undefined : null}
          onClose={() => { setImportSource(null); setImportText(''); setImportApp(null); setImportCampaignName(null); }} onImported={() => undefined}
          onOpenCampaign={campaignsPageId ? (sequenceId) => {
            const next = new URLSearchParams(searchParams);
            next.set('campagne', sequenceId);
            setSearchParams(next);
          } : undefined} />
      )}
    </div>
  );
};
